import React from "react";
import { Calculator, RotateCcw } from "lucide-react";
import { formatINR, formatSqft, valuationBreakdown } from "@/lib/visitSchema";

function Row({ label, formula, value, testId }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5 border-b border-dashed border-slate-200 last:border-0">
      <div>
        <div className="text-sm text-slate-800">{label}</div>
        <div className="text-[11px] text-slate-500">{formula}</div>
      </div>
      <div className="num text-sm font-semibold text-slate-900 whitespace-nowrap" data-testid={testId}>{value}</div>
    </div>
  );
}

/* Live valuation math: plot area × land rate + built-up × construction cost. Read-only; recomputes on every keystroke. */
export default function ValuationBreakdown({ form, onReset }) {
  const bd = valuationBreakdown(form);
  const manual = Boolean(form.valuation_manual);
  const shown = form.valuation_amount_inr === "" || form.valuation_amount_inr == null ? null : Number(form.valuation_amount_inr);
  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-4" data-testid="valuation-breakdown">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-emerald-800 mb-2">
        <Calculator className="h-3.5 w-3.5" /> Valuation calculator
      </div>
      <Row label="Total plot area" formula="Plot length × Plot width" value={formatSqft(bd.plot_area_sqft)} testId="valuation-plot-area" />
      <Row label="Total plot value" formula="Plot area × Land rate (₹/sq ft)" value={formatINR(bd.plot_value_inr)} testId="valuation-plot-value" />
      <Row label="Total construction value" formula="Built-up area × Cost of construction (₹/sq ft)" value={formatINR(bd.construction_value_inr)} testId="valuation-construction-value" />
      <div className="mt-3 flex items-end justify-between gap-3">
        <div>
          <div className="text-xs text-slate-600">Valuation amount (Plot value + Construction value)</div>
          {manual ? (
            <div className="text-[11px] text-amber-700 mt-0.5" data-testid="valuation-manual-note">
              Manual override · calculated {formatINR(bd.computed_valuation_inr)}
              {bd.computed_valuation_inr != null && (
                <button type="button" onClick={onReset} className="ml-2 inline-flex items-center gap-1 underline underline-offset-2 hover:text-amber-900" data-testid="valuation-reset-button">
                  <RotateCcw className="h-3 w-3" /> Reset to calculated
                </button>
              )}
            </div>
          ) : (
            <div className="text-[11px] text-emerald-700 mt-0.5" data-testid="valuation-auto-note">Auto-calculated · edit the field below to override</div>
          )}
        </div>
        <div className="num text-2xl font-semibold text-emerald-800 whitespace-nowrap" data-testid="valuation-total">{formatINR(shown ?? bd.computed_valuation_inr)}</div>
      </div>
    </div>
  );
}
